import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Box,
} from '@mui/material';
import mammoth from 'mammoth';
import { useAppContext } from '../../../context/AppContext';

const DOCX_PATH = '/DietistPro_TermsAndConditions_Privacy.docx';

/**
 * TermsModal
 * ----------
 * Fetches the terms & conditions docx from the public folder,
 * converts it to HTML via mammoth, and displays it inside a MUI Dialog.
 */
const TermsModal = ({ open, onClose }) => {
    const { appColors } = useAppContext();
    const [html, setHtml] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!open || html) return;

        const load = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await fetch(DOCX_PATH);
                if (!response.ok) throw new Error('Failed to fetch document');
                const arrayBuffer = await response.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                setHtml(result.value);
            } catch (err) {
                console.error('TermsModal: failed to load document', err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [open, html]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            aria-labelledby="terms-modal-title"
        >
            <DialogTitle
                id="terms-modal-title"
                sx={{ backgroundColor: appColors.primary, color: '#fff', fontWeight: 600 }}
            >
                Terms &amp; Conditions
            </DialogTitle>

            <DialogContent dividers>
                {loading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress sx={{ color: appColors.primary }} />
                    </Box>
                )}
                {error && !loading && (
                    <p style={{ color: 'red' }}>
                        Unable to load the document. Please try again later.
                    </p>
                )}
                {!loading && !error && (
                    <div
                        className="terms-modal-content"
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        backgroundColor: appColors.primary,
                        '&:hover': { backgroundColor: appColors.primaryHover },
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TermsModal;
